/**
 * AggregatedScoreDashboard — Full-page professional dashboard for a single run's
 * aggregated score.  Route: /results/run-score-dashboard/:runId
 *
 * Sections:
 *  1. Hero — large gauge + final score + metadata
 *  2. Pipeline Overview Cards — one card per pipeline with weight progress bar
 *  3. Entity Breakdown (Level 1) — expandable accordion per pipeline
 *  4. Config Snapshot — auditable footer showing config used for calculation
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TextField from "@mui/material/TextField";
import {
  IAggregatedRunScore,
  IPipelineScoreBreakdown,
  IPipelineScoreEntry,
} from "../../types/Scoring.type";
import {
  getAggregatedRunScore,
  generateAggregatedRunScore,
} from "../../services/scoringService";
import ScoreBadge from "../../components/Scoring/ScoreBadge";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import ButtonComponent from "../../components/Button.Component";
import {
  getScoreColor,
  getScoreBg,
  getScoreLabel,
  getStatusColor,
  formatPercent,
  PIPELINE_LABELS,
  PIPELINE_COLORS,
} from "../../components/Scoring/scoreTheme";
import { getExecutionSubGroupName } from "../../utils/executionGroup";
import useExecutionSubGroupMap from "../../hooks/useExecutionSubGroupMap";

/**
 * Per-pipeline empty-state copy used in the Entity Breakdown section when
 * a pipeline type is included in the run but produced no scoreable entries.
 */
const PIPELINE_EMPTY_MESSAGES: Record<string, string> = {
  validation: "No data available for validations",
  comparison: "No data available for comparisons",
  research_query: "No data available for research queries",
};

/**
 * EmptyState — polished placeholder used when an API returns no rows.
 * Replaces skeleton loaders that previously made the page look mid-load.
 */
const EmptyState: React.FC<{
  title: string;
  description?: string;
  accentColor?: string;
  compact?: boolean;
}> = ({ title, description, accentColor, compact = false }) => (
  <Paper
    variant="outlined"
    sx={{
      p: compact ? { xs: 2.5, md: 3 } : { xs: 4, md: 5 },
      borderRadius: 2,
      borderStyle: "dashed",
      borderColor: accentColor ? `${accentColor}55` : "grey.300",
      bgcolor: accentColor ? `${accentColor}08` : "grey.50",
      textAlign: "center",
    }}
  >
    <InfoOutlinedIcon
      sx={{
        fontSize: compact ? 28 : 36,
        color: accentColor || "text.disabled",
        mb: 1,
      }}
    />
    <Typography
      variant={compact ? "body2" : "subtitle1"}
      sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}
    >
      {title}
    </Typography>
    {description && (
      <Typography variant="caption" color="text.secondary">
        {description}
      </Typography>
    )}
  </Paper>
);

/* ── Hero Gauge ── */
const HeroGauge: React.FC<{ score: number }> = ({ score }) => {
  const color = getScoreColor(score);
  const pct = score * 100;
  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress
        variant="determinate"
        value={100}
        size={180}
        thickness={5}
        sx={{ color: "rgba(0,0,0,0.06)", position: "absolute" }}
      />
      <CircularProgress
        variant="determinate"
        value={pct}
        size={180}
        thickness={5}
        sx={{
          color,
          transition: "all 0.8s cubic-bezier(.4,0,.2,1)",
          "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: 180,
          height: 180,
          px: 2.5,
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: "1.75rem",
            fontWeight: 800,
            color,
            lineHeight: 1.1,
            whiteSpace: "nowrap",
          }}
        >
          {pct.toFixed(1)}%
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color,
            mt: 0.5,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
        >
          {getScoreLabel(score)}
        </Typography>
      </Box>
    </Box>
  );
};

/* ── Weight Progress Bar ── */
const WeightBar: React.FC<{ value: number; total: number; color: string }> = ({
  value,
  total,
  color,
}) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          flex: 1,
          height: 8,
          borderRadius: 4,
          bgcolor: `${color}20`,
          "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 4 },
        }}
      />
      <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 36 }}>
        {pct.toFixed(0)}%
      </Typography>
    </Box>
  );
};

/* ── Snapshot helpers ── */
// Keys shared across every pipeline (shown in each card's tooltip)
const SHARED_SNAPSHOT_KEYS = new Set([
  "default_entity_factor",
  "default_entity_severity",
]);

// Humanize snake_case → Title Case for display
const humanizeKey = (key: string): string =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Return snapshot entries relevant to a given pipeline type
const getPipelineSnapshotEntries = (
  pipelineType: string,
  snapshot?: Record<string, any> | null,
): Array<[string, any]> => {
  if (!snapshot) return [];
  const keyword = pipelineType.toLowerCase();
  return Object.entries(snapshot).filter(([key]) => {
    const k = key.toLowerCase();
    return k.includes(keyword) || SHARED_SNAPSHOT_KEYS.has(k);
  });
};

/* ── Reusable tooltip content row — hardened for nested objects ── */
const TooltipConfigRow: React.FC<{ entryKey: string; value: any }> = ({
  entryKey,
  value,
}) => {
  const isObject =
    value !== null && typeof value === "object" && !Array.isArray(value);
  return (
    <Box sx={{ mb: 0.75, "&:last-child": { mb: 0 } }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "grey.400",
          fontSize: 11,
          wordBreak: "break-word",
          lineHeight: 1.3,
        }}
      >
        {humanizeKey(entryKey)}
      </Typography>
      {isObject ? (
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 0.25,
            p: 0.75,
            fontFamily:
              '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: 11,
            lineHeight: 1.4,
            color: "common.white",
            bgcolor: "rgba(255,255,255,0.08)",
            borderRadius: 0.75,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: 220,
            overflowY: "auto",
          }}
        >
          {JSON.stringify(value, null, 2)}
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "common.white",
            wordBreak: "break-word",
          }}
        >
          {value === null || value === undefined ? "—" : String(value)}
        </Typography>
      )}
    </Box>
  );
};

/* ── Main Component ── */
const AggregatedScoreDashboard: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IAggregatedRunScore | null>(null);
  const [recalculating, setRecalculating] = useState(false);
  const [dateFilter, setDateFilter] = useState<string>("");
  const executionSubGroupMap = useExecutionSubGroupMap();

  // Append the run as the trailing entity segment in the global breadcrumb,
  // matching the pattern used by ComparisonResultDashboard / GenericResultDashboard.
  useEffect(() => {
    const resolvedRunId = data?.run_id ?? (runId ? Number(runId) : null);
    setCurrentBreadcrumbPage({
      name: data?.run_name ?? "Run",
      id: resolvedRunId,
      url: resolvedRunId ? `/run-instance/view/${resolvedRunId}` : "",
    });
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null, url: "" });
    };
  }, [data, runId, setCurrentBreadcrumbPage]);

  // Accepts an explicit date so caller controls the query param independent
  // of dateFilter state timing (avoids stale-closure / extra-render fetches).
  const fetchScore = useCallback(
    async (date?: string): Promise<IAggregatedRunScore | null> => {
      if (!runId) return null;
      setLoading(true);
      try {
        const result = await getAggregatedRunScore(
          Number(runId),
          date || undefined,
        );
        setData(result);
        return result;
      } catch {
        setData(null);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [runId],
  );

  // Initial load: fetch latest, then sync the date picker to the returned date
  // so the control reflects the most recent available calculation for this run.
  useEffect(() => {
    (async () => {
      const result = await fetchScore();
      if (result) {
        const latest = result.score_date ?? result.date ?? "";
        if (latest) setDateFilter(latest);
      }
    })();
  }, [fetchScore]);

  const handleDateChange = (newDate: string) => {
    setDateFilter(newDate);
    fetchScore(newDate);
  };

  const handleClearDate = () => {
    setDateFilter("");
    fetchScore();
  };

  const handleRecalculate = async () => {
    if (!runId) return;
    setRecalculating(true);
    try {
      await generateAggregatedRunScore(Number(runId), dateFilter || undefined);
      await fetchScore(dateFilter);
    } finally {
      setRecalculating(false);
    }
  };

  if (loading && !data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  /**
   * Navigate to the specific execution dashboard based on pipeline type.
   *  - comparison  → /rules-execution-history/:executionId/:executionId/dashboard
   *  - validation  → /validation-execution-history/validate-result/:executionId
   *  - research_query → /research-execution-history/:entityId/:executionId/dashboard
   */
  const navigateToExecution = (
    pipelineType: string,
    entityId: number,
    executionId: number,
  ) => {
    let url = "";
    switch (pipelineType) {
      case "comparison":
        url = `/rules-execution-history/${executionId}/${executionId}/dashboard`;
        break;
      case "validation":
        url = `/validation-execution-history/validate-result/${executionId}`;
        break;
      case "research_query":
        url = `/research-execution-history/${entityId}/${executionId}/dashboard`;
        break;
      default:
        return;
    }
    window.open(url, "_blank");
  };

  // ── Normalise field names (API may return either variant) ──
  // Dashboard renders in "empty-state" mode when data is null so users can still
  // see the scaffolding and trigger a recalculation for the selected date.
  const isEmpty = !data;
  const finalScore = data?.final_score ?? data?.score ?? 0;
  const scoreDate = data?.score_date ?? data?.date ?? "—";
  const breakdown = data?.score_details ?? data?.details;
  const pipelines: IPipelineScoreBreakdown[] = breakdown?.pipelines || [];
  const pipelinesIncluded: string[] = breakdown?.pipelines_included || [];
  const totalWeight = pipelines.reduce((s, p) => s + p.pipeline_weight, 0);
  const hasAnyFailures = pipelines.some((p) => p.has_failed_jobs);

  return (
    <Box sx={{ width: "100%", py: { xs: 2, md: 3 } }}>
      {/* ─── Top Bar ─── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          flexWrap: "wrap",
          rowGap: 1.5,
          columnGap: 2,
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            label="Score Date"
            type="date"
            value={dateFilter}
            onChange={(e) => handleDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
            disabled={loading}
          />
          {dateFilter && (
            <ButtonComponent
              handelClickEvent={handleClearDate}
              className="btn-orange btn-border"
            >
              Clear
            </ButtonComponent>
          )}
          <ButtonComponent
            handelClickEvent={handleRecalculate}
            className="btn-orange"
            disabled={recalculating}
          >
            <RefreshIcon sx={{ mr: "4px" }} />
            {recalculating ? "Recalculating…" : "Recalculate"}
          </ButtonComponent>
        </Box>
      </Box>

      {/* ─── HERO SECTION ─── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 4 },
          mb: 3,
          borderRadius: 3,
          background: getScoreBg(finalScore),
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 2.5, md: 4 },
          border: `1px solid ${getScoreColor(finalScore)}30`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <HeroGauge score={finalScore} />
        </Box>
        <Box sx={{ flex: 1, width: "100%", minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: getScoreColor(finalScore),
              }}
            >
              Final Aggregated Score
            </Typography>
            <Tooltip
              arrow
              placement="right"
              title={
                <Box sx={{ p: 0.5, maxWidth: 320 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      fontWeight: 700,
                      color: "common.white",
                      mb: 0.75,
                    }}
                  >
                    How the final score is calculated
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 0.75,
                      fontFamily:
                        '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                      fontSize: 11.5,
                      lineHeight: 1.5,
                      color: "common.white",
                      bgcolor: "rgba(255,255,255,0.08)",
                      borderRadius: 0.75,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {
                      "Final Score = Σ (pipeline_score × pipeline_weight)\n             / Σ pipeline_weights"
                    }
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.75,
                      color: "grey.300",
                      fontSize: 11,
                    }}
                  >
                    Weighted mean across all included pipelines. See the
                    Configuration Snapshot below for the exact weights applied.
                  </Typography>
                </Box>
              }
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{
                  color: "text.secondary",
                  cursor: "help",
                  "&:hover": { color: getScoreColor(finalScore) },
                }}
              />
            </Tooltip>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">
                Score Date
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, wordBreak: "break-word" }}
              >
                {scoreDate}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">
                Calculated On
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, wordBreak: "break-word" }}
              >
                {data?.calculated_on
                  ? new Date(data.calculated_on).toLocaleString()
                  : "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">
                Pipelines Included
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  mt: 0.5,
                }}
              >
                {pipelinesIncluded.map((pt: string) => (
                  <Chip
                    key={pt}
                    label={PIPELINE_LABELS[pt] || pt}
                    size="small"
                    sx={{
                      bgcolor: `${PIPELINE_COLORS[pt] || "#9e9e9e"}20`,
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      maxWidth: "100%",
                    }}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {hasAnyFailures ? (
                  <Chip
                    icon={<WarningAmberIcon />}
                    label="Has Failed Jobs"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    icon={<CheckCircleOutlineIcon />}
                    label="All Jobs OK"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* ─── PIPELINE OVERVIEW CARDS (Level 2) ─── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, mt: 1 }}>
        Pipeline Overview
      </Typography>
      {isEmpty || pipelines.length === 0 ? (
        <Box sx={{ mb: 4 }}>
          <EmptyState
            title="No pipeline scores available"
            description="Trigger a recalculation, or pick a different score date to load pipeline scores for this run."
          />
        </Box>
      ) : (
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          {pipelines.map((p) => {
            const color = PIPELINE_COLORS[p.pipeline_type] || "#9e9e9e";
            const contribution =
              totalWeight > 0
                ? (p.pipeline_weight * p.pipeline_score) / totalWeight
                : 0;
            return (
              <Grid item xs={12} sm={6} md={4} key={p.pipeline_type}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    borderColor: `${color}40`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: 4,
                      height: "100%",
                      bgcolor: color,
                    }}
                  />
                  <Box sx={{ pl: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                        minHeight: 28,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={
                          PIPELINE_LABELS[p.pipeline_type] || p.pipeline_type
                        }
                      >
                        {PIPELINE_LABELS[p.pipeline_type] || p.pipeline_type}
                      </Typography>
                      {(() => {
                        const entries = getPipelineSnapshotEntries(
                          p.pipeline_type,
                          data?.config_snapshot as
                            | Record<string, any>
                            | null
                            | undefined,
                        );
                        if (entries.length === 0) return null;
                        return (
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              <Box
                                sx={{ p: 0.5, maxWidth: 340, minWidth: 200 }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    fontWeight: 700,
                                    color: "common.white",
                                    mb: 0.75,
                                  }}
                                >
                                  {PIPELINE_LABELS[p.pipeline_type] ||
                                    p.pipeline_type}{" "}
                                  — Config Snapshot
                                </Typography>
                                {entries.map(([k, v]) => (
                                  <TooltipConfigRow
                                    key={k}
                                    entryKey={k}
                                    value={v}
                                  />
                                ))}
                              </Box>
                            }
                          >
                            <InfoOutlinedIcon
                              fontSize="small"
                              sx={{
                                color,
                                opacity: 0.75,
                                cursor: "help",
                                flexShrink: 0,
                                "&:hover": { opacity: 1 },
                              }}
                            />
                          </Tooltip>
                        );
                      })()}
                      {p.has_failed_jobs && (
                        <Tooltip title="Some jobs have failed">
                          <WarningAmberIcon color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "2rem",
                          fontWeight: 800,
                          color: getScoreColor(p.pipeline_score),
                        }}
                      >
                        {formatPercent(p.pipeline_score)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        pipeline score
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Weight: {p.pipeline_weight}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.entries.length} entities
                      </Typography>
                    </Box>
                    <WeightBar
                      value={p.pipeline_weight}
                      total={totalWeight}
                      color={color}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      Contribution to final: {formatPercent(contribution)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ─── ENTITY BREAKDOWN (Level 1) ─── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, mt: 1 }}>
        Entity Breakdown
      </Typography>
      {isEmpty || pipelines.length === 0 ? (
        pipelinesIncluded.length > 0 ? (
          // One typed empty state per pipeline declared in the snapshot, so
          // users see exactly which pipelines are missing data instead of a
          // single generic message.
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {pipelinesIncluded.map((pt) => (
              <EmptyState
                key={`entity-empty-${pt}`}
                title={
                  PIPELINE_EMPTY_MESSAGES[pt] ||
                  `No data available for ${PIPELINE_LABELS[pt] || pt}`
                }
                accentColor={PIPELINE_COLORS[pt]}
                compact
              />
            ))}
          </Box>
        ) : (
          <EmptyState
            title="No entity-level scores available"
            description="No pipelines were included in this run's score snapshot."
          />
        )
      ) : (
        pipelines.map((p) => {
          const color = PIPELINE_COLORS[p.pipeline_type] || "#9e9e9e";
          return (
            <Accordion
              key={p.pipeline_type}
              defaultExpanded
              variant="outlined"
              sx={{
                mb: 1,
                borderRadius: "8px !important",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: `${color}08`,
                  "&:hover": { bgcolor: `${color}12` },
                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    my: 1,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {PIPELINE_LABELS[p.pipeline_type] || p.pipeline_type}
                  </Typography>
                  <Chip
                    label={`${p.entries.length} entities`}
                    size="small"
                    sx={{ mr: 1, flexShrink: 0 }}
                  />
                  <Box sx={{ flexShrink: 0 }}>
                    <ScoreBadge score={p.pipeline_score} size="small" />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {p.entries.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <EmptyState
                      title={
                        PIPELINE_EMPTY_MESSAGES[p.pipeline_type] ||
                        `No data available for ${PIPELINE_LABELS[p.pipeline_type] || p.pipeline_type}`
                      }
                      accentColor={color}
                      compact
                    />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "grey.50" }}>
                          <TableCell sx={{ fontWeight: 700 }}>Entity</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>
                            Execution Sub Group
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            Score
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>
                            Severity
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            Factor
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            Weighted Score
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>
                            Job Status
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            Execution
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {p.entries.map(
                          (entry: IPipelineScoreEntry, idx: number) => (
                            <TableRow
                              key={`${entry.entity_id}-${entry.execution_id}-${entry.execution_sub_group_code ?? "default"}-${idx}`}
                              sx={{ "&:hover": { bgcolor: "action.hover" } }}
                            >
                              <TableCell>
                                <span
                                  className="nav-link"
                                  onClick={() =>
                                    navigateToExecution(
                                      entry.pipeline_type,
                                      entry.entity_id,
                                      entry.execution_id,
                                    )
                                  }
                                >
                                  {entry.entity_name}
                                </span>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block" }}
                                >
                                  #{entry.entity_id} · Execution #
                                  {entry.execution_id}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {getExecutionSubGroupName(entry, executionSubGroupMap) ? (
                                  <Chip
                                    label={getExecutionSubGroupName(entry, executionSubGroupMap)}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontWeight: 600,
                                      fontSize: "0.72rem",
                                    }}
                                  />
                                ) : (
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                  >
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    color: getScoreColor(entry.score),
                                  }}
                                >
                                  {formatPercent(entry.score)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={entry.severity}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                {entry.factor.toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                <Typography sx={{ fontWeight: 600 }}>
                                  {entry.weighted_score.toFixed(4)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={entry.job_status}
                                  size="small"
                                  color={getStatusColor(entry.job_status)}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="caption">
                                  #{entry.execution_id}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      {/* ─── CONFIG SNAPSHOT (Auditability) ─── */}
      <Paper variant="outlined" sx={{ p: 2.5, mt: 3, mb: 2, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
            flexWrap: "wrap",
          }}
        >
          <SettingsIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Configuration Snapshot
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: "auto" }}
          >
            Values used at the time of calculation
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {data?.config_snapshot ? (
          <Grid container spacing={2}>
            {Object.entries(data.config_snapshot).map(([key, value]) => {
              const isObject =
                value !== null &&
                typeof value === "object" &&
                !Array.isArray(value);
              return (
                <Grid
                  item
                  xs={12}
                  sm={isObject ? 12 : 6}
                  md={isObject ? 6 : 3}
                  key={key}
                >
                  <Box
                    sx={{
                      p: 1.25,
                      bgcolor: "grey.50",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "grey.200",
                      height: "100%",
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mb: 0.5,
                        wordBreak: "break-word",
                        lineHeight: 1.3,
                      }}
                    >
                      {key
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </Typography>
                    {isObject ? (
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1.25,
                          fontFamily:
                            '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                          fontSize: 12,
                          lineHeight: 1.45,
                          color: "text.primary",
                          bgcolor: "#f6f8fa",
                          border: "1px solid",
                          borderColor: "grey.300",
                          borderRadius: 1,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowX: "auto",
                          maxHeight: 280,
                          overflowY: "auto",
                        }}
                      >
                        {JSON.stringify(value, null, 2)}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {value === null || value === undefined
                          ? "—"
                          : String(value)}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box
            sx={{
              py: 3,
              textAlign: "center",
              color: "text.secondary",
              fontStyle: "italic",
            }}
          >
            <Typography variant="body2">
              No configuration found for this date.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AggregatedScoreDashboard;
